import React, { useState, useEffect, useRef, useCallback } from 'react';
import { kunciSimbol, generateUrutanSoal, hitungSkorSubstitusi } from './bankSoalSubstitusi';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

const WAKTU_MENIT = 5;
const WAKTU_TOTAL = WAKTU_MENIT * 60;
const JUMLAH_ITEM = 80; // lebih banyak dari yang bisa diselesaikan — memang begitu formatnya

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function getBadge(skor) {
  if (skor >= 90) return { label: 'Luar Biasa', color: 'text-green-700 bg-green-100 border-green-300' };
  if (skor >= 75) return { label: 'Baik', color: 'text-blue-700 bg-blue-100 border-blue-300' };
  if (skor >= 60) return { label: 'Cukup', color: 'text-yellow-700 bg-yellow-100 border-yellow-300' };
  return { label: 'Perlu Latihan', color: 'text-red-700 bg-red-100 border-red-300' };
}

function StripKunci({ ukuranKecil }) {
  return (
    <div className={`grid grid-cols-5 sm:grid-cols-10 gap-1.5 ${ukuranKecil ? '' : 'mb-2'}`}>
      {kunciSimbol.map(k => (
        <div key={k.id} className="bg-white rounded-lg border border-gray-200 p-1 flex flex-col items-center">
          <div className="w-full aspect-square" dangerouslySetInnerHTML={{ __html: k.simbol }} />
          <div className="w-3/5 aspect-square -mt-0.5" dangerouslySetInnerHTML={{ __html: k.kode }} />
        </div>
      ))}
    </div>
  );
}

function BannerLanjutkanSubstitusi({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const totalSoal = progresTersimpan.soalList?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-4 border-2 border-indigo-400 bg-indigo-50">
      <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-indigo-800 mb-3">
        Baru sampai soal <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-indigo-300 text-indigo-600 text-sm font-semibold hover:bg-indigo-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg p-6">
        <div className="text-center mb-5">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🔣</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Tes Substitusi Simbol</h1>
          <p className="text-gray-400 text-sm mt-1">
            {tryoutMode ? 'Bagian dari Tryout Resmi' : 'Cocokkan simbol dengan kode sesuai kunci'}
          </p>
        </div>

        <BannerLanjutkanSubstitusi progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="rounded-xl p-4 mb-4 border-2 border-indigo-200 bg-indigo-50">
          <p className="text-xs font-bold uppercase tracking-wide text-indigo-700 mb-1">📌 {tryoutMode ? 'Bagian Tryout' : 'Info Modul'}</p>
          <p className="text-sm text-indigo-800 leading-relaxed">
            Mengukur kecepatan & ketelitian kerja (mirip Kecermatan) — {tryoutMode ? 'ikut dihitung ke skor Tryout resmi.' : 'sudah ikut dihitung ke skor Tryout resmi.'}
          </p>
        </div>

        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Kunci Jawaban (selalu tampil saat tes)</p>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-2 mb-4">
          <StripKunci />
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 text-sm text-gray-600 leading-relaxed">
          Setiap simbol muncul satu per satu di layar. Tekan tombol kode yang sesuai dengan kunci di atas, secepat dan seteliti mungkin. Kerjakan sebanyak yang kamu bisa dalam <b>{WAKTU_MENIT} menit</b> — wajar kalau tidak sempat menyelesaikan semuanya.
        </div>

        <div className="flex gap-3">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">
            ← Kembali
          </button>
          <button onClick={onMulai} className="flex-grow py-3 rounded-xl font-bold border-2 border-indigo-400 bg-indigo-50 text-indigo-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    </div>
  );
}

function LayarHasil({ hasil, onUlang, onBack, tryoutMode, tryoutId }) {
  const badge = getBadge(hasil.skor);
  useEffect(() => {
    catatHasilTes('substitusi', hasil.skor, { benar: hasil.benar, salah: hasil.salah, dikerjakan: hasil.dikerjakan, total: hasil.total }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">{hasil.skor >= 75 ? '🏆' : hasil.skor >= 60 ? '👍' : '📚'}</div>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Hasil Tes Substitusi Simbol</h2>
          <div className="text-6xl font-extrabold text-indigo-600 mb-1">{hasil.skor}</div>
          <p className="text-gray-400 text-sm mb-3">Akurasi (dari {hasil.dikerjakan} soal dikerjakan)</p>
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold border ${badge.color}`}>{badge.label}</span>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-4 mb-2">
            <div className="bg-indigo-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${hasil.skor}%` }} />
          </div>
          <div className="grid grid-cols-4 gap-2 text-center text-xs mt-4">
            <div className="bg-blue-50 rounded-lg py-2">
              <p className="font-bold text-blue-700 text-base">{hasil.dikerjakan}</p>
              <p className="text-gray-500">Dikerjakan</p>
            </div>
            <div className="bg-green-50 rounded-lg py-2">
              <p className="font-bold text-green-700 text-base">{hasil.benar}</p>
              <p className="text-gray-500">Benar</p>
            </div>
            <div className="bg-red-50 rounded-lg py-2">
              <p className="font-bold text-red-700 text-base">{hasil.salah}</p>
              <p className="text-gray-500">Salah</p>
            </div>
            <div className="bg-gray-50 rounded-lg py-2">
              <p className="font-bold text-gray-600 text-base">{hasil.total - hasil.dikerjakan}</p>
              <p className="text-gray-500">Tak Sempat</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <div className="flex gap-3">
            <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 transition">
              🔄 Ulangi
            </button>
            <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">
              ← Menu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SubstitusiModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase] = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [soalList, setSoalList] = useState([]);
  const [indeks, setIndeks] = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(WAKTU_TOTAL);
  const [flashSalah, setFlashSalah] = useState(false);
  const timerRef = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('substitusi', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catatan: soal di sini digenerate acak setiap sesi (kunciId per item baru
  // dipilih random saat generateUrutanSoal dipanggil), berbeda dari modul lain
  // yang bisa lookup ulang dari bank soal statis via ID. Karena itu di sini kita
  // simpan soalList APA ADANYA (bukan cuma ID) — tetap ringan karena isinya cuma
  // {id, kunciId}, tanpa SVG/gambar (SVG-nya ada di kunciSimbol yang statis).
  useAutosaveTes(fase === 'tes', 'substitusi', tryoutId, {
    soalList,
    indeks,
    jawabanUser,
    waktuSisa,
  });

  const handleMulai = useCallback(() => {
    hapusProgresTes('substitusi', tryoutId);
    setProgresTersimpan(null);
    const soal = generateUrutanSoal(JUMLAH_ITEM);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser([]);
    setWaktuSisa(WAKTU_TOTAL);
    setFase('tes');
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    setSoalList(progresTersimpan.soalList || []);
    setIndeks(progresTersimpan.indeks || 0);
    setJawabanUser(progresTersimpan.jawabanUser || []);
    setWaktuSisa(progresTersimpan.waktuSisa || WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('substitusi', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          hapusProgresTes('substitusi', tryoutId);
          setFase('hasil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [fase, tryoutId]);

  const handleJawab = useCallback((kodeId) => {
    setJawabanUser(prev => {
      const soalIni = soalList[indeks];
      if (!soalIni) return prev;
      return [...prev, { id: soalIni.id, jawaban: kodeId }];
    });
    const soalIni = soalList[indeks];
    if (soalIni && kodeId !== soalIni.kunciId) {
      setFlashSalah(true);
      setTimeout(() => setFlashSalah(false), 200);
    }
    if (indeks + 1 >= soalList.length) {
      clearInterval(timerRef.current);
      hapusProgresTes('substitusi', tryoutId);
      setFase('hasil');
    } else {
      setIndeks(i => i + 1);
    }
  }, [indeks, soalList, tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro');
    setSoalList([]);
    setIndeks(0);
    setJawabanUser([]);
    setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') return (
    <LayarIntro
      onMulai={handleMulai}
      onBack={onBack}
      tryoutMode={tryoutMode}
      progresTersimpan={progresTersimpan}
      onLanjutkan={handleLanjutkan}
      onMulaiBaru={handleMulaiBaruDariBanner}
    />
  );

  if (fase === 'hasil') {
    const hasil = hitungSkorSubstitusi(soalList, jawabanUser);
    return <LayarHasil hasil={hasil} onUlang={handleUlang} onBack={onBack} tryoutMode={tryoutMode} tryoutId={tryoutId} />;
  }

  const soal = soalList[indeks];
  if (!soal) return null;
  const progress = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter = waktuSisa < 30 ? 'bg-red-500' : waktuSisa < 90 ? 'bg-yellow-400' : 'bg-green-500';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg ${waktuSisa < 30 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              Soal <span className="text-indigo-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">🔣 Substitusi</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 text-center">Kunci Jawaban</p>
        <div className="bg-white border border-gray-200 rounded-xl p-2 mb-4">
          <StripKunci ukuranKecil />
        </div>

        <div className={`bg-white rounded-2xl shadow p-8 mb-4 flex items-center justify-center transition-colors ${flashSalah ? 'bg-red-50' : ''}`}>
          <div className="w-28 h-28" dangerouslySetInnerHTML={{ __html: kunciSimbol.find(k => k.id === soal.kunciId)?.simbol }} />
        </div>

        <p className="text-center text-xs text-gray-400 mb-3">Simbol di atas cocok dengan kode yang mana?</p>

        <div className="grid grid-cols-5 gap-2">
          {kunciSimbol.map(k => (
            <button
              key={k.id}
              onClick={() => handleJawab(k.id)}
              className="bg-white border-2 border-gray-200 rounded-xl p-2 hover:border-indigo-400 hover:bg-indigo-50 active:scale-95 transition"
            >
              <div className="w-full aspect-square" dangerouslySetInnerHTML={{ __html: k.kode }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
